/**
 * AttendanceSyncListPage — Issue #188.
 *
 * Lists HR attendance sync run records. Read-only page; trigger is via
 * provider configuration page (out-of-scope for this Issue's minimal Admin UI).
 *
 * 红线：
 *   - 仅使用 useHrAttendanceSyncRuns hook，不手写 fetch。
 *   - 不展示 credentialCipher / metadataJson 中的密钥；只展示 hint。
 *   - tenantId / orgId 不展示。
 */
import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_ATTENDANCE_SYNC_STATUS_LABELS,
  useHrAttendanceSyncRuns,
  type HrAttendanceSyncRunDto,
} from '@/hooks/useHrIssue188';

const STATUS_TONE: Record<HrAttendanceSyncRunDto['status'], string> = {
  running: 'bg-slate-100 text-slate-700',
  succeeded: 'bg-green-100 text-green-700',
  partial: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-700',
};

function fmt(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

export default function AttendanceSyncListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.ATTENDANCE_READ);

  const { items, total, loading, error, fetchRuns } = useHrAttendanceSyncRuns();
  const [statusFilter, setStatusFilter] = useState<HrAttendanceSyncRunDto['status'] | ''>('');
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!canRead) return;
    void fetchRuns({
      status: statusFilter || undefined,
      page,
      limit,
    });
  }, [canRead, statusFilter, page, fetchRuns]);

  if (!canRead) return <AccessDenied resource="HR 考勤同步记录" />;

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">考勤同步记录</h1>
          <p className="text-sm text-slate-500">
            考勤原始记录只保留不覆盖；修正通过调整记录体现。
          </p>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(
                (e.target.value as HrAttendanceSyncRunDto['status']) || '',
              );
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(Object.entries(HR_ATTENDANCE_SYNC_STATUS_LABELS) as [
              HrAttendanceSyncRunDto['status'],
              string,
            ][]).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-3 py-2 w-32">Provider</th>
                <th className="px-3 py-2 w-24">触发</th>
                <th className="px-3 py-2 w-24">状态</th>
                <th className="px-3 py-2 w-40">区间开始</th>
                <th className="px-3 py-2 w-40">区间结束</th>
                <th className="px-3 py-2 w-20 text-right">成功</th>
                <th className="px-3 py-2 w-20 text-right">失败</th>
                <th className="px-3 py-2 w-20 text-right">跳过</th>
                <th className="px-3 py-2">错误概要</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <div className="font-medium">{row.providerCode}</div>
                    <div className="text-xs text-slate-500">{row.providerType}</div>
                  </td>
                  <td className="px-3 py-2">{row.trigger}</td>
                  <td className="px-3 py-2">
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                        STATUS_TONE[row.status],
                      )}
                    >
                      {HR_ATTENDANCE_SYNC_STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">{fmt(row.rangeStart)}</td>
                  <td className="px-3 py-2">{fmt(row.rangeEnd)}</td>
                  <td className="px-3 py-2 text-right">{row.successCount}</td>
                  <td className="px-3 py-2 text-right">{row.failureCount}</td>
                  <td className="px-3 py-2 text-right">{row.skippedCount}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {row.errorSummary ?? '—'}
                  </td>
                </tr>
              ))}
              {items.length === 0 && !loading ? (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-slate-400">
                    没有同步记录
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      <footer className="flex items-center justify-between text-sm text-slate-600">
        <span>
          共 {total} 条 / 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </footer>
    </div>
  );
}
